import type { PageComponent } from '@/core/features/a-dynamic-page/dynamic-page-pacts/dynamic-page-type';

import React from 'react';

import dynamic from 'next/dynamic';

import { getComponentId } from '@/core/features/a-component/services/component-id-service';
import { useLayoutId } from '@/core/features/app/app-atoms';
import { useDeviceoutput } from '@/core/features/cookies/use-device-output';
import ErrorBoundaryWrapper from '@/core/features/error-boundary/error-boundary-wrapper';
import { logger } from '@/core/features/logger/logger';
import { triggerMapError } from '@/core/features/native-app-bridge/native-app-bridge-service';
import useMapOverlayHistoryState from '@/features/map/map-container/use-map-overlay-history';

const MapOverlayDesktop = dynamic(
    () => import(/* webpackChunkName: "MapOverlayDesktop" */ '@/features/map/map-container/map-overlay-desktop'),
);

const MapOverlay = dynamic(
    () => import(/* webpackChunkName: "MapOverlay" */ '@/features/map/map-container/map-overlay'),
);

const MapComponent = dynamic(
    () => import(/* webpackChunkName: "MapComponent" */ '@/features/map/map-container/map-component'),
);

export default function MapContainer({ components }: { components: PageComponent[] }) {
    const { isDesktopView } = useDeviceoutput();
    const { isOverlayOpen, openOverlay } = useMapOverlayHistoryState();
    const layoutId = useLayoutId();

    /** Rendering MapComponent before everything else and regardless of the isOverlayOpen state
     * has unfortunately led to hydration errors, hence the small workaround */
    React.useEffect(() => {
        if (isOverlayOpen || layoutId !== 'Mobile_Map_WebView_V1') {
            return;
        }
        openOverlay();
    }, [isOverlayOpen, layoutId, openOverlay]);

    if (!isOverlayOpen) {
        return null;
    }

    const mapDataComponent = components.find((component) => component['@type'] === 'MapDataV5');

    if (!mapDataComponent) {
        logger.warn('No MapDataV5 to open Map Overlay', { url: location.href });
        return null;
    }

    const componentId = getComponentId(mapDataComponent['@id']);

    if (layoutId === 'Mobile_Map_WebView_V1') {
        return (
            <ErrorBoundaryWrapper onError={triggerMapError}>
                <MapComponent componentId={componentId} />;
            </ErrorBoundaryWrapper>
        );
    }

    return (
        <ErrorBoundaryWrapper fallbackRender={() => null}>
            {isDesktopView ? <MapOverlayDesktop componentId={componentId} /> : <MapOverlay componentId={componentId} />}
        </ErrorBoundaryWrapper>
    );
}
